import { Injectable, NgZone, OnDestroy } from '@angular/core';

const STORAGE_KEY = 'synced_mailbox_connected';
const CHANNEL = 'synced-mailbox-connected';

/**
 * SD-1229: delivers "mailbox connected" when the popup could not post it to us.
 *
 * The OAuth confirmation page tells its opener with window.opener.postMessage. Microsoft's
 * sign-in pages set a Cross-Origin-Opener-Policy that cuts the popup off from its opener, so
 * after an Outlook connect window.opener is null and nothing arrives: no toast, no refreshed
 * inbox list, no Inbox Automator prompt until a reload.
 *
 * Where the app and the API share an origin (the API is served under /api/), the page also
 * writes the message to localStorage and a BroadcastChannel. This replays it on this window
 * as the same 'message' event, from our own origin, so every existing listener -- Emails,
 * Settings > Integrations, the workflow builders -- handles it unchanged.
 *
 * Each message carries a nonce. One the popup did manage to post directly is not replayed,
 * and a replay is held back briefly so the direct message, sent a moment later, wins.
 */
@Injectable({ providedIn: 'root' })
export class MailboxConnectBridgeService implements OnDestroy {
  private started = false;
  private channel: BroadcastChannel | null = null;
  private readonly seen = new Set<string>();

  constructor(private zone: NgZone) { }

  start(): void {
    if (this.started) { return; }
    this.started = true;
    window.addEventListener('message', this.onMessage);
    window.addEventListener('storage', this.onStorage);
    try {
      this.channel = new BroadcastChannel(CHANNEL);
      this.channel.onmessage = (e: MessageEvent) => this.replay(e.data);
    } catch { /* no BroadcastChannel: storage alone */ }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('storage', this.onStorage);
    try { this.channel?.close(); } catch { /* closed */ }
  }

  /** Direct posts (and our own replays) mark their nonce as delivered. */
  private readonly onMessage = (e: MessageEvent) => {
    const nonce = e?.data?.nonce;
    if (typeof nonce === 'string' && nonce) { this.seen.add(nonce); }
  };

  private readonly onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) { return; }
    try { this.replay(JSON.parse(e.newValue)); } catch { /* not ours */ }
  };

  private replay(data: any): void {
    const nonce = data?.nonce;
    if (!data || data.type !== 'GMAIL_CONNECTED' || typeof nonce !== 'string' || !nonce) { return; }
    // Both fallbacks fire for one connect; the direct post follows them by a moment.
    setTimeout(() => {
      if (this.seen.has(nonce)) { return; }
      this.seen.add(nonce);
      this.zone.run(() => window.dispatchEvent(new MessageEvent('message', { data, origin: location.origin })));
    }, 400);
  }
}
